import { Avatar, Box } from '@goodneighbor/ui';
import type { OrganizationDetails } from '@goodneighbor/api/routes/organizations/organizations.schema';
import { formatWebsiteLabel, mergeClassNames } from '@/services/organization';
import styles from './organization-contact-card.module.scss';

type ContactInformationCardProps = {
  organization: OrganizationDetails;
};

export const ContactInformationCard = ({
  organization
}: ContactInformationCardProps) => {
  const { contact } = organization;
  const contactName = contact
    ? `${contact.firstName} ${contact.lastName}`
    : null;
  const websiteLabel = organization.website
    ? formatWebsiteLabel(organization.website)
    : null;
  const hasCityLine = !!(
    organization.city ||
    organization.region ||
    organization.postalCode
  );
  const hasDetails = Boolean(
    organization.addressLine1 ||
      organization.address ||
      hasCityLine ||
      contact?.phone ||
      contact?.email ||
      organization.website
  );

  return (
    <Box
      className={styles.contactCard}
      tone="surface"
      padding="lg"
      radius="lg"
      border="none"
      shadow="xs"
    >
      <div className={styles.contactCardHeadingRow}>
        <p className={styles.cardHeading}>Contact Information</p>
      </div>
      <div className={styles.contactHeader}>
        <div className={styles.contactAvatarWrapper}>
          <Avatar
            size="xl"
            src={contact?.avatarUrl}
            alt={`${organization.name} contact avatar`}
            name={contactName ?? organization.name}
            className={styles.contactAvatar}
          />
        </div>
        <div className={styles.contactHeadingBlock}>
          <div className={styles.contactNameRow}>
            <p className={styles.contactName}>
              {contactName ?? organization.name}
            </p>
          </div>
          {contact?.email && (
            <p className={styles.contactHeaderEmail}>{contact.email}</p>
          )}
        </div>
      </div>
      {hasDetails ? (
        <div className={styles.contactDetails}>
          {(organization.addressLine1 ||
            organization.address ||
            hasCityLine) && (
            <div className={styles.contactItem}>
              <div className={styles.contactCopy}>
                <p className={styles.contactLabel}>Address</p>
                <p className={styles.contactValue}>
                  {organization.addressLine1 ?? organization.address}
                  {!!(
                    (organization.addressLine1 || organization.address) &&
                    hasCityLine
                  ) && <br />}
                  {organization.city}
                  {organization.city &&
                  (organization.region || organization.postalCode)
                    ? ', '
                    : ''}
                  {organization.region}
                  {organization.postalCode ? ` ${organization.postalCode}` : ''}
                </p>
              </div>
            </div>
          )}
          {contact?.phone && (
            <div className={styles.contactItem}>
              <div className={styles.contactCopy}>
                <p className={styles.contactLabel}>Phone</p>
                <p className={styles.contactValue}>{contact.phone}</p>
              </div>
            </div>
          )}
          {contact?.email && (
            <div className={styles.contactItem}>
              <div className={styles.contactCopy}>
                <p className={styles.contactLabel}>Email</p>
                <p className={styles.contactValue}>{contact.email}</p>
              </div>
            </div>
          )}
          {organization.website && websiteLabel && (
            <div className={styles.contactItem}>
              <div className={styles.contactCopy}>
                <p className={styles.contactLabel}>Website</p>
                <a
                  href={organization.website}
                  className={mergeClassNames(
                    styles.contactLink,
                    styles.contactLinkWebsite
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {websiteLabel}
                </a>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className={styles.emptyState}>More contact details coming soon.</p>
      )}
    </Box>
  );
};
